import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthLink as Link } from '../auth/AuthContext';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../api';
import AuthCard from '../components/AuthCard';
import Spinner from '../components/Spinner';

/**
 * Страница по ссылке из письма. Ничего не спрашивает: пользователь уже подтвердил адрес
 * тем, что открыл письмо и нажал кнопку, - просить его нажать ещё одну незачем.
 */
const VerifyEmailPage = () => {
    const [params] = useSearchParams();
    const token = params.get('token') || '';

    const [state, setState] = useState('checking'); // 'checking' | 'done' | 'invalid'
    const [email, setEmail] = useState(null);
    const started = useRef(false);

    useEffect(() => {
        if (!token) {
            setState('invalid');
            return;
        }
        // В режиме разработки React монтирует компонент дважды, а токен одноразовый:
        // второй запрос получил бы отказ по уже использованной ссылке.
        if (started.current) return;
        started.current = true;

        // Сначала кука с CSRF-токеном, иначе POST ниже отвергается.
        api.get('/api/auth/csrf')
            .catch(() => {})
            .then(() => api.post('/api/auth/email/verify', { token }))
            .then(res => {
                setEmail(res.data.email);
                setState('done');
            })
            .catch(() => setState('invalid'));
    }, [token]);

    if (state === 'checking') {
        return (
            <AuthCard title="Подтверждение адреса">
                <div className="flex items-center justify-center gap-3 text-gray-500 py-4">
                    <Spinner size={22} /> Подтверждаем…
                </div>
            </AuthCard>
        );
    }

    if (state === 'invalid') {
        return (
            <AuthCard title="Ссылка не работает">
                <AlertTriangle size={44} className="text-amber-500 mx-auto mb-4" />
                <p className="text-gray-600 text-sm leading-relaxed">
                    Ссылка для подтверждения устарела или уже использована. Войдите в аккаунт и
                    запросите новое письмо в разделе профиля.
                </p>
                <Link
                    to="/login"
                    className="mt-6 inline-block bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium no-underline hover:bg-blue-700"
                >
                    Войти
                </Link>
            </AuthCard>
        );
    }

    return (
        <AuthCard title="Адрес подтверждён">
            <CheckCircle size={44} className="text-emerald-600 mx-auto mb-4" />
            <p className="text-gray-600 text-sm leading-relaxed">
                {email ? <span className="font-medium text-gray-900">{email}</span> : 'Адрес'} подтверждён.
                Теперь доступны восстановление пароля и уведомления.
            </p>
            <Link
                to="/dashboard/profile"
                className="mt-6 inline-block bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium no-underline hover:bg-blue-700"
            >
                В личный кабинет
            </Link>
        </AuthCard>
    );
};

export default VerifyEmailPage;
